import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { z } from 'zod';
import { styleIntelligenceApi } from '@/services/api/agentApi';
import { ensureOptionalAgentServiceAvailable } from '@/services/api/agentServiceAvailability';
import type { IStyleProfile, StyleProfileBrowseCard, StyleProfileVisibility } from '@/types';

type StyleApiResponse<T = unknown> = {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
};

const StyleActivationResultSchema = z.object({
  directiveMemoryIds: z.array(z.string()),
  warnings: z.array(z.string()).optional(),
});

type StyleActivationResult = z.infer<typeof StyleActivationResultSchema>;

function isOrgWideActiveProfile(profile: IStyleProfile): boolean {
  return profile.status === 'active' && (profile.activeProjectIds?.length ?? 0) === 0;
}

function getOrgWideActiveProfile(profiles: IStyleProfile[]): IStyleProfile | null {
  return profiles.find(isOrgWideActiveProfile) || null;
}

function getProjectActiveProfile(profiles: IStyleProfile[], projectId?: string): IStyleProfile | null {
  return projectId
    ? profiles.find(profile => profile.activeProjectIds?.includes(projectId)) || null
    : null;
}

function getEffectiveActiveProfile(profiles: IStyleProfile[], projectId?: string): IStyleProfile | null {
  return getProjectActiveProfile(profiles, projectId) || getOrgWideActiveProfile(profiles);
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    const response = Reflect.get(error, 'response');
    if (response && typeof response === 'object') {
      const data = Reflect.get(response, 'data');
      if (data && typeof data === 'object') {
        const responseError = Reflect.get(data, 'error');
        if (typeof responseError === 'string' && responseError.length > 0) {
          return responseError;
        }

        const responseMessage = Reflect.get(data, 'message');
        if (typeof responseMessage === 'string' && responseMessage.length > 0) {
          return responseMessage;
        }
      }
    }

    const message = Reflect.get(error, 'message');
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  }

  return fallback;
}

function getResponseErrorMessage(response: StyleApiResponse, fallback: string): string {
  return response.error || response.message || fallback;
}

function requireResponseData<T>(response: StyleApiResponse<T>, fallback: string): T {
  if (!response.success || response.data === undefined) {
    throw new Error(getResponseErrorMessage(response, fallback));
  }
  return response.data;
}

function requireResponseSuccess(response: StyleApiResponse, fallback: string): void {
  if (!response.success) {
    throw new Error(getResponseErrorMessage(response, fallback));
  }
}

// ── State Interface ─────────────────────────────────────────────────

type BrowseSharedProfilesParams = {
  search?: string;
  tags?: string;
  sort?: string;
  page?: number;
  limit?: number;
  append?: boolean;
};

interface StyleIntelligenceState {
  // Data
  profiles: IStyleProfile[];
  activeProfile: IStyleProfile | null;

  // Sharing / Browse
  sharedProfiles: StyleProfileBrowseCard[];
  sharedProfilesTotal: number;
  isBrowsing: boolean;

  // Loading states
  isAnalyzing: boolean;
  isLoading: boolean;

  // Error
  error: string | null;

  // Actions — Analysis
  analyzeScreenshot: (image: string, mimeType: string, name?: string) => Promise<void>;
  analyzeUrl: (url: string, name?: string) => Promise<void>;

  // Actions — CRUD
  fetchProfiles: (projectId?: string) => Promise<void>;
  updateProfile: (id: string, updates: Partial<IStyleProfile>) => Promise<void>;
  deleteProfile: (id: string) => Promise<void>;

  // Actions — Activation
  activateProfile: (id: string, projectId?: string) => Promise<string[]>;
  deactivateProfile: (id: string, projectId?: string) => Promise<void>;

  // Actions — Sharing
  browseSharedProfiles: (params?: BrowseSharedProfilesParams) => Promise<void>;
  importProfile: (id: string) => Promise<IStyleProfile | null>;
  updateVisibility: (id: string, visibility: StyleProfileVisibility, sharedWithOrgIds?: string[]) => Promise<void>;
}

// ── Store ────────────────────────────────────────────────────────────

export const useStyleIntelligenceStore = create<StyleIntelligenceState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      profiles: [],
      activeProfile: null,
      sharedProfiles: [],
      sharedProfilesTotal: 0,
      isBrowsing: false,
      isAnalyzing: false,
      isLoading: false,
      error: null,

      // ── Analysis Actions ──────────────────────────────────────

      analyzeScreenshot: async (image: string, mimeType: string, name?: string) => {
        if (get().isAnalyzing) return;

        set((state) => {
          state.isAnalyzing = true;
          state.error = null;
        });

        try {
          const response = await styleIntelligenceApi.analyzeScreenshot({ image, mimeType, name });
          const profile = requireResponseData(response, 'Failed to analyze screenshot');
          set((state) => {
            state.profiles.push(profile);
            state.isAnalyzing = false;
          });
        } catch (error) {
          const message = getErrorMessage(error, 'Failed to analyze screenshot');
          set((state) => {
            state.error = message;
            state.isAnalyzing = false;
          });
          throw new Error(message);
        }
      },

      analyzeUrl: async (url: string, name?: string) => {
        if (get().isAnalyzing) return;

        set((state) => {
          state.isAnalyzing = true;
          state.error = null;
        });

        try {
          const response = await styleIntelligenceApi.analyzeUrl({ url, name });
          const profile = requireResponseData(response, 'Failed to analyze URL');
          set((state) => {
            state.profiles.push(profile);
            state.isAnalyzing = false;
          });
        } catch (error) {
          const message = getErrorMessage(error, 'Failed to analyze URL');
          set((state) => {
            state.error = message;
            state.isAnalyzing = false;
          });
          throw new Error(message);
        }
      },

      // ── CRUD Actions ──────────────────────────────────────────

      fetchProfiles: async (projectId?: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          if (!(await ensureOptionalAgentServiceAvailable())) {
            set((state) => {
              state.profiles = [];
              state.activeProfile = null;
              state.isLoading = false;
              state.error = null;
            });
            return;
          }

          const response = await styleIntelligenceApi.getProfiles(
            projectId ? { projectId } : undefined
          );
          set((state) => {
            if (response.success) {
              state.profiles = response.data;
              state.activeProfile = getEffectiveActiveProfile(response.data, projectId);
            }
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error = getErrorMessage(error, 'Failed to fetch profiles');
            state.isLoading = false;
          });
        }
      },

      updateProfile: async (id: string, updates: Partial<IStyleProfile>) => {
        set((state) => {
          state.error = null;
        });

        try {
          const response = await styleIntelligenceApi.updateProfile(id, updates);
          const profile = requireResponseData(response, 'Failed to update profile');
          set((state) => {
            const index = state.profiles.findIndex(p => p.profileId === id);
            if (index !== -1) {
              state.profiles[index] = profile;
            }
            if (state.activeProfile?.profileId === id) {
              state.activeProfile = profile;
            }
          });
        } catch (error) {
          const message = getErrorMessage(error, 'Failed to update profile');
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      deleteProfile: async (id: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          const response = await styleIntelligenceApi.deleteProfile(id);
          requireResponseSuccess(response, 'Failed to delete profile');
          set((state) => {
            state.profiles = state.profiles.filter(p => p.profileId !== id);
            if (state.activeProfile?.profileId === id) {
              state.activeProfile = null;
            }
          });
        } catch (error) {
          const message = getErrorMessage(error, 'Failed to delete profile');
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      // ── Activation Actions ────────────────────────────────────

      activateProfile: async (id: string, projectId?: string) => {
        set((state) => {
          state.error = null;
        });

        const existingProfile = get().profiles.find(profile => profile.profileId === id);
        if (projectId && existingProfile && isOrgWideActiveProfile(existingProfile)) {
          return ['This profile is already active as the org-wide baseline. Duplicate it before activating it for a single project.'];
        }

        try {
          const response = await styleIntelligenceApi.activateProfile(id, projectId);
          const activation: StyleActivationResult = StyleActivationResultSchema.parse(
            requireResponseData(response, 'Failed to activate profile')
          );
          set((state) => {
            if (projectId) {
              // Per-project: remove projectId from any other profile's activeProjectIds
              for (const profile of state.profiles) {
                if (profile.profileId !== id && profile.activeProjectIds?.includes(projectId)) {
                  profile.activeProjectIds = profile.activeProjectIds.filter(p => p !== projectId);
                  if (profile.activeProjectIds.length === 0) {
                    profile.status = 'draft';
                    profile.directiveMemoryIds = [];
                  }
                }
              }
              // Add projectId to this profile
              const index = state.profiles.findIndex(p => p.profileId === id);
              if (index !== -1) {
                const ids = state.profiles[index].activeProjectIds || [];
                if (!ids.includes(projectId)) ids.push(projectId);
                state.profiles[index].activeProjectIds = ids;
                state.profiles[index].status = 'active';
                state.profiles[index].directiveMemoryIds = activation.directiveMemoryIds;
                state.activeProfile = state.profiles[index];
              }
            } else {
              // Org-wide: only replace the current org-wide active profile.
              for (const profile of state.profiles) {
                if (profile.profileId !== id && isOrgWideActiveProfile(profile)) {
                  profile.status = 'draft';
                  profile.directiveMemoryIds = [];
                }
              }
              const index = state.profiles.findIndex(p => p.profileId === id);
              if (index !== -1) {
                state.profiles[index].status = 'active';
                state.profiles[index].activeProjectIds = [];
                state.profiles[index].directiveMemoryIds = activation.directiveMemoryIds;
                state.activeProfile = state.profiles[index];
              }
            }
          });
          return activation.warnings ?? [];
        } catch (error) {
          const message = getErrorMessage(error, 'Failed to activate profile');
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      deactivateProfile: async (id: string, projectId?: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          const response = await styleIntelligenceApi.deactivateProfile(id, projectId);
          requireResponseSuccess(response, 'Failed to deactivate profile');
          set((state) => {
            const index = state.profiles.findIndex(p => p.profileId === id);
            if (index !== -1) {
              if (projectId) {
                // Remove projectId from activeProjectIds
                const activeProjectIds = (state.profiles[index].activeProjectIds || []).filter(p => p !== projectId);
                state.profiles[index].activeProjectIds = activeProjectIds;
                if (activeProjectIds.length === 0) {
                  state.profiles[index].status = 'draft';
                  state.profiles[index].directiveMemoryIds = [];
                }
              } else {
                state.profiles[index].status = 'draft';
                state.profiles[index].activeProjectIds = [];
                state.profiles[index].directiveMemoryIds = [];
              }
            }
            if (state.activeProfile?.profileId === id) {
              state.activeProfile = projectId
                ? getEffectiveActiveProfile(state.profiles, projectId)
                : getOrgWideActiveProfile(state.profiles);
            }
          });
        } catch (error) {
          const message = getErrorMessage(error, 'Failed to deactivate profile');
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },

      // ── Sharing Actions ─────────────────────────────────────────

      browseSharedProfiles: async (params) => {
        const { append, ...apiParams } = params ?? {};
        const browseParams = params ? apiParams : undefined;

        set((state) => {
          state.isBrowsing = true;
          state.error = null;
        });

        try {
          const response = await styleIntelligenceApi.browseSharedProfiles(browseParams);
          set((state) => {
            if (response.success) {
              state.sharedProfiles = append
                ? [...state.sharedProfiles, ...response.data]
                : response.data;
              state.sharedProfilesTotal = response.total ?? 0;
            }
            state.isBrowsing = false;
          });
        } catch (error) {
          set((state) => {
            state.error = getErrorMessage(error, 'Failed to browse shared profiles');
            state.isBrowsing = false;
          });
        }
      },

      importProfile: async (id: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          const response = await styleIntelligenceApi.importProfile(id);
          if (response.success) {
            set((state) => {
              state.profiles.push(response.data);
            });
            return response.data;
          }
          return null;
        } catch (error) {
          set((state) => {
            state.error = getErrorMessage(error, 'Failed to import profile');
          });
          return null;
        }
      },

      updateVisibility: async (id: string, visibility: StyleProfileVisibility, sharedWithOrgIds?: string[]) => {
        set((state) => {
          state.error = null;
        });

        try {
          const updates: Partial<IStyleProfile> = {
            visibility,
            sharedWithOrgIds: visibility === 'shared' ? (sharedWithOrgIds ?? []) : [],
          };
          const response = await styleIntelligenceApi.updateProfile(id, updates);
          const profile = requireResponseData(response, 'Failed to update sharing settings');
          set((state) => {
            const index = state.profiles.findIndex(p => p.profileId === id);
            if (index !== -1) {
              state.profiles[index] = profile;
            }
            if (state.activeProfile?.profileId === id) {
              state.activeProfile = profile;
            }
          });
        } catch (error) {
          const message = getErrorMessage(error, 'Failed to update sharing settings');
          set((state) => {
            state.error = message;
          });
          throw new Error(message);
        }
      },
    }))
  )
);
